// Раздел «Учётки и доступ»: владелец управляет доступом в CRM без правки users.json
// руками. Виден и открыт РОВНО ОДНОЙ учётке — как касса (rights.владелец): право
// раздавать права дороже самой кассы.
//
// ЧЕГО ЭКРАН НЕ ПОКАЗЫВАЕТ И НЕ ДЕЛАЕТ, и это правила BFF, а не забывчивость экрана:
//   • хеш пароля не приходит с сервера вовсе — только признак «пароль выдан»;
//   • пароль уходит в теле POST по HTTPS (как на входе) и нигде не остаётся:
//     ни в адресе, ни в журнале, ни в ответе;
//   • свою учётку нельзя заблокировать, разжаловать или отвязать — сервер откажет
//     (защита от локаута), поэтому у своей строки эти органы выключены заранее;
//   • заготовка mkusers_bulk применяется ЗАБЛОКИРОВАННОЙ и без паролей: выдача
//     пароля и разблокировка — отдельные явные шаги по каждому человеку.

import { useState } from 'react'

import type { Правка, Учётка } from '../api/adminUsers'
import {
  РОЛИ, useВыдачаПароля, useПравка, useПрименениеЗаготовки, useУчётки,
} from '../api/adminUsers'
import { useMe } from '../api/hooks'
import { QueryState } from '../components/QueryState'
import AdminNabory from '../components/AdminNabory'
import AdminCardFields from '../components/AdminCardFields'

/** Вкладки раздела: учётки (кто и с какими правами), наборы (что каждому видно)
 *  и поля карточек (владелец сам заводит поля сделок/компаний/контактов/судов). */
type Вкладка = 'учётки' | 'наборы' | 'поля'

export default function AdminUsers() {
  const [вкладка, setВкладка] = useState<Вкладка>('учётки')
  return (
    <>
      <div className="page-head">
        <h1>Учётки и доступ</h1>
        <span className="spacer" />
        <div style={{ display: 'flex', gap: 6 }}>
          {([['учётки', 'Учётки'], ['наборы', 'Наборы'],
             ['поля', 'Поля карточек']] as [Вкладка, string][])
            .map(([код, подпись]) => (
              <button key={код} onClick={() => setВкладка(код)}
                      aria-pressed={вкладка === код}
                      style={{ fontWeight: вкладка === код ? 600 : 400 }}>
                {подпись}
              </button>
            ))}
        </div>
      </div>
      {вкладка === 'учётки' ? <ВкладкаУчёток />
        : вкладка === 'наборы' ? <AdminNabory />
          : <AdminCardFields />}
    </>
  )
}

function ВкладкаУчёток() {
  const запрос = useУчётки()
  const я = useMe().data?.username ?? ''
  const д = запрос.data
  const [парольДля, setПарольДля] = useState('')     // логин, которому выдаём пароль
  const [правкаДля, setПравкаДля] = useState('')     // логин с раскрытой панелью правки

  return (
    <>
      <div className="page-head">
        <span className="spacer" />
        <span className="hint">
          {д ? `учёток: ${д.users.length}, без пароля: `
            + `${д.users.filter((u) => !u.has_password).length}, заблокировано: `
            + `${д.users.filter((u) => u.disabled).length}` : ''}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 16 }}>
        Раздел открыт только учётке владельца. Все действия пишутся в журнал доступа;
        меняется один файл учёток CRM (users.json) — в Bitrix и 1С отсюда не уходит
        ничего. Права пересчитываются по файлу на каждом запросе: перезапуск и
        повторный вход людям не нужны.
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Логин</th>
                  <th>Имя</th>
                  <th className="nowrap">Роль</th>
                  <th className="nowrap">Набор</th>
                  <th className="nowrap">Сотрудник Bitrix</th>
                  <th className="nowrap">Пароль</th>
                  <th className="nowrap">Состояние</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(д?.users ?? []).map((u) => (
                  <Строка key={u.username} u={u} сам={u.username === я}
                          наборы={д?.sets ?? []}
                          парольОткрыт={парольДля === u.username}
                          правкаОткрыта={правкаДля === u.username}
                          наПароль={() => { setПарольДля(парольДля === u.username ? '' : u.username); setПравкаДля('') }}
                          наПравку={() => { setПравкаДля(правкаДля === u.username ? '' : u.username); setПарольДля('') }} />
                ))}
                {д && д.users.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Учёток нет</div>
                      <div className="empty-hint">
                        Файл учёток пуст — так не бывает на живом сервере: как минимум
                        владелец в нём есть. Проверьте USERS_FILE в .env.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {д && <Заготовка д={д} />}
      </QueryState>
    </>
  )
}

function Строка({ u, сам, наборы, парольОткрыт, правкаОткрыта, наПароль, наПравку }: {
  u: Учётка
  сам: boolean
  наборы: { code: string; title: string }[]
  парольОткрыт: boolean
  правкаОткрыта: boolean
  наПароль: () => void
  наПравку: () => void
}) {
  const правка = useПравка()
  const защита = 'Свою учётку менять нельзя: вы заперли бы сами себя (защита от локаута)'

  return (
    <>
      <tr>
        <td data-label="Логин"><b>{u.username}</b>{сам && <span className="badge" style={{ marginLeft: 6 }}>это вы</span>}</td>
        <td data-label="Имя">{u.name}</td>
        <td data-label="Роль" className="nowrap">
          <select value={u.role} disabled={сам || правка.isPending}
                  title={сам ? защита : 'Роль действует со следующего запроса человека'}
                  onChange={(e) => правка.mutate({ username: u.username,
                                                   changes: { role: e.target.value } })}>
            {РОЛИ.map((р) => <option key={р.код} value={р.код}>{р.имя}</option>)}
          </select>
        </td>
        <td data-label="Набор" className="nowrap">
          {/* Набор — рабочее место (стартовая сводка и состав меню), а не право:
              права по-прежнему решает роль. Меняется в панели «править». */}
          {u.set
            ? (наборы.find((н) => н.code === u.set)?.title ?? u.set)
            : <span className="muted" title="Набора нет — видно всё, что даёт роль">—</span>}
        </td>
        <td data-label="Сотрудник Bitrix" className="nowrap">
          {u.bitrix_user_id
            ? <>#{u.bitrix_user_id}</>
            : <span className="muted" title="Роли head и user без привязки не видят ничего:
зону не с чем сопоставить">не привязан</span>}
          {(u.see_users.length > 0 || u.see_depts.length > 0) && (
            <div className="hint">
              {u.see_users.length > 0 && <>+люди {u.see_users.join(', ')} </>}
              {u.see_depts.length > 0 && <>+отделы {u.see_depts.join(', ')}</>}
            </div>
          )}
        </td>
        <td data-label="Пароль" className="nowrap">
          {u.has_password
            ? <span className="badge won">выдан</span>
            : <span className="badge work">не выдан</span>}
        </td>
        <td data-label="Состояние" className="nowrap">
          {u.disabled
            ? <span className="badge lose">заблокирована</span>
            : <span className="badge won">действует</span>}
        </td>
        <td data-label="Действия" className="nowrap">
          <button onClick={наПароль} disabled={правка.isPending}>
            {парольОткрыт ? 'закрыть' : 'выдать пароль'}
          </button>{' '}
          <button onClick={() => правка.mutate({ username: u.username,
                                                 changes: { disabled: !u.disabled } })}
                  disabled={сам || правка.isPending}
                  title={сам ? защита : undefined}>
            {u.disabled ? 'разблокировать' : 'заблокировать'}
          </button>{' '}
          <button onClick={наПравку}>{правкаОткрыта ? 'свернуть' : 'править'}</button>
        </td>
      </tr>
      {правка.error && (
        <tr><td colSpan={8} className="error">{(правка.error as Error).message}</td></tr>
      )}
      {парольОткрыт && (
        <tr><td colSpan={8}><ФормаПароля логин={u.username} заблокирована={u.disabled}
                                          готово={наПароль} /></td></tr>
      )}
      {правкаОткрыта && (
        <tr><td colSpan={8}><ФормаПравки u={u} сам={сам} наборы={наборы}
                                         готово={наПравку} /></td></tr>
      )}
    </>
  )
}

/**
 * Выдача пароля: два поля, правило mkuser (непустой и повтор совпал; минимальной длины
 * у mkuser нет — и здесь не выдумана). Значения живут только в состоянии формы и в теле
 * одного POST; после успеха стираются.
 */
function ФормаПароля({ логин, заблокирована, готово }: {
  логин: string
  заблокирована: boolean
  готово: () => void
}) {
  const выдача = useВыдачаПароля()
  const [п1, setП1] = useState('')
  const [п2, setП2] = useState('')

  const отправить = () => {
    выдача.mutate({ username: логин, password: п1, password2: п2 }, {
      onSuccess: () => { setП1(''); setП2('') },
    })
  }

  if (выдача.isSuccess) {
    return (
      <div className="notice">
        Пароль учётке <b>{логин}</b> выдан (в файле — только его pbkdf2-хеш).
        {заблокирована && (
          <> Учётка ПОКА ЗАБЛОКИРОВАНА: вход не заработает, пока вы её не
            разблокируете — это отдельная кнопка, а не часть выдачи пароля.</>
        )}{' '}
        <button onClick={готово}>закрыть</button>
      </div>
    )
  }

  return (
    <div className="card">
      <b>Пароль для «{логин}»</b>
      <div className="hint" style={{ margin: '4px 0 8px' }}>
        Пароль скажите человеку сами — система его больше нигде не покажет и не хранит.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label>
          Пароль{' '}
          <input type="password" autoComplete="new-password" value={п1}
                 onChange={(e) => setП1(e.target.value)} />
        </label>
        <label>
          Повтор{' '}
          <input type="password" autoComplete="new-password" value={п2}
                 onChange={(e) => setП2(e.target.value)} />
        </label>
        <button onClick={отправить} disabled={выдача.isPending}>выдать</button>
        <button onClick={готово} disabled={выдача.isPending}>отмена</button>
      </div>
      {выдача.error && (
        <div className="error" style={{ marginTop: 8 }}>
          {(выдача.error as Error).message}
        </div>
      )}
    </div>
  )
}

/** Правка набора, привязки, ручной зоны и имени. Списки — числа через запятую, как у mkuser. */
function ФормаПравки({ u, сам, наборы, готово }: {
  u: Учётка
  сам: boolean
  наборы: { code: string; title: string }[]
  готово: () => void
}) {
  const правка = useПравка()
  const [bx, setBx] = useState(u.bitrix_user_id === null ? '' : String(u.bitrix_user_id))
  const [набор, setНабор] = useState(u.set ?? '')
  const [люди, setЛюди] = useState(u.see_users.join(', '))
  const [отделы, setОтделы] = useState(u.see_depts.join(', '))
  const [имя, setИмя] = useState(u.name)
  const [ошибка, setОшибка] = useState('')

  const числа = (s: string): number[] | null => {
    const из = s.split(/[,;]/).map((x) => x.trim()).filter(Boolean)
    const вышло: number[] = []
    for (const x of из) {
      const n = Number(x)
      if (!Number.isInteger(n) || n <= 0) return null
      вышло.push(n)
    }
    return вышло
  }

  const сохранить = () => {
    setОшибка('')
    const свои = числа(люди)
    const свои2 = числа(отделы)
    if (свои === null || свои2 === null) {
      setОшибка('Наборы видимости — числовые id через запятую.')
      return
    }
    const bxЧисло = bx.trim() === '' ? null : Number(bx.trim())
    if (bxЧисло !== null && (!Number.isInteger(bxЧисло) || bxЧисло <= 0)) {
      setОшибка('Привязка к сотруднику — числовой id Bitrix или пусто.')
      return
    }
    const changes: Правка = {
      set: набор.trim(), bitrix_user_id: bxЧисло, see_users: свои, see_depts: свои2,
      name: имя,
    }
    правка.mutate({ username: u.username, changes }, { onSuccess: готово })
  }

  return (
    <div className="card">
      <b>Правка «{u.username}»</b>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                    marginTop: 8 }}>
        <label>Имя <input value={имя} onChange={(e) => setИмя(e.target.value)} /></label>
        <label title="Набор разделов (рабочее место): стартовая сводка и меню. Пусто — видно всё, что даёт роль">
          Набор{' '}
          {наборы.length > 0 ? (
            <select value={набор} onChange={(e) => setНабор(e.target.value)}>
              <option value="">без набора</option>
              {наборы.map((н) => <option key={н.code} value={н.code}>{н.title}</option>)}
            </select>
          ) : (
            <input value={набор} onChange={(e) => setНабор(e.target.value)}
                   placeholder="код набора" style={{ width: 110 }} />
          )}
        </label>
        <label title={сам ? 'Свою привязку менять нельзя: по ней проверяется учётка владельца' : 'id сотрудника Bitrix; пусто — отвязать'}>
          Сотрудник Bitrix{' '}
          <input value={bx} onChange={(e) => setBx(e.target.value)} disabled={сам}
                 style={{ width: 90 }} />
        </label>
        <label title="Кого человек видит дополнительно к своей зоне (id сотрудников через запятую)">
          +люди <input value={люди} onChange={(e) => setЛюди(e.target.value)}
                       style={{ width: 140 }} />
        </label>
        <label title="Какие отделы (с подотделами) человек видит дополнительно (id через запятую)">
          +отделы <input value={отделы} onChange={(e) => setОтделы(e.target.value)}
                         style={{ width: 140 }} />
        </label>
        <button onClick={сохранить} disabled={правка.isPending}>сохранить</button>
        <button onClick={готово} disabled={правка.isPending}>отмена</button>
      </div>
      {(ошибка || правка.error) && (
        <div className="error" style={{ marginTop: 8 }}>
          {ошибка || (правка.error as Error).message}
        </div>
      )}
    </div>
  )
}

function Заготовка({ д }: { д: NonNullable<ReturnType<typeof useУчётки>['data']> }) {
  const применение = useПрименениеЗаготовки()
  if (д.draft.total === 0) {
    return (
      <div className="card">
        <b>Заготовка учёток</b>
        <div className="hint" style={{ marginTop: 4 }}>
          Файла заготовки нет. Его собирает mkusers_bulk.py на сервере из действующих
          сотрудников зеркала — без паролей и с блокировкой.
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>Заготовка учёток: {д.draft.total}, можно завести: {д.draft.applicable}</b>
        <span className="spacer" />
        <button disabled={д.draft.applicable === 0 || применение.isPending}
                onClick={() => применение.mutate({})}>
          завести все ({д.draft.applicable})
        </button>
      </div>
      <div className="hint" style={{ marginBottom: 8 }}>
        Заведённые учётки появляются ЗАБЛОКИРОВАННЫМИ, без пароля и с ролью «Сотрудник».
        Выдать пароль и разблокировать — отдельные шаги по каждому человеку.
      </div>
      {применение.error && (
        <div className="error" style={{ marginBottom: 8 }}>
          {(применение.error as Error).message}
        </div>
      )}
      {применение.data && (
        <div className="notice" style={{ marginBottom: 8 }}>
          Заведено: {применение.data.applied.length}
          {применение.data.skipped.length > 0 && (
            <>, пропущено: {применение.data.skipped.length} (
              {применение.data.skipped.map((s) => `${s.username} — ${s.why}`).join('; ')})</>
          )}
        </div>
      )}
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Логин</th>
              <th>Имя</th>
              <th className="nowrap">Сотрудник Bitrix</th>
              <th>Судьба</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {д.draft.entries.map((z) => (
              <tr key={z.username}>
                <td data-label="Логин">{z.username}</td>
                <td data-label="Имя">{z.name}</td>
                <td data-label="Сотрудник Bitrix" className="nowrap">
                  {z.bitrix_user_id ? `#${z.bitrix_user_id}` : '—'}
                </td>
                <td data-label="Судьба">
                  {z.status === 'новая'
                    ? <span className="badge work">можно завести</span>
                    : <span className="muted">{z.status}</span>}
                </td>
                <td data-label="Действия" className="nowrap">
                  {z.status === 'новая' && (
                    <button disabled={применение.isPending}
                            onClick={() => применение.mutate({ username: z.username })}>
                      завести
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
